/**
 * IndexedDBUsingSessionStorage
 */

import { SessionData } from '@shared/types/session';
import { serializeSessionXML, parseSessionXML } from './xmlParser';

const DB_NAME = 'ardour-web';
const DB_VERSION = 2;
const STORE_SESSIONS = 'sessions';
const STORE_AUDIO_FILES = 'audio_files';
const STORE_PEAK_FILES = 'peak_files';
const STORE_METADATA = 'metadata';
const STORE_PROJECTS = 'projects';

// interface DB {
//   db: IDBDatabase;
// }

let dbInstance: IDBDatabase | null = null;

/**
 * IndexedDBInitialize
 */
export async function initDB(): Promise<IDBDatabase> {
  if (dbInstance) {
    return dbInstance;
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error('Failed to open IndexedDB'));
    };

    request.onblocked = () => {
      reject(new Error('IndexedDB upgrade blocked'));
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      dbInstance.onversionchange = () => {
        dbInstance?.close();
        dbInstance = null;
      };
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Sessions store
      if (!db.objectStoreNames.contains(STORE_SESSIONS)) {
        const sessionsStore = db.createObjectStore(STORE_SESSIONS, { keyPath: 'id' });
        sessionsStore.createIndex('name', 'name', { unique: false });
        sessionsStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // Audio files store
      if (!db.objectStoreNames.contains(STORE_AUDIO_FILES)) {
        const audioStore = db.createObjectStore(STORE_AUDIO_FILES, { keyPath: 'id' });
        audioStore.createIndex('sessionId', 'sessionId', { unique: false });
        audioStore.createIndex('path', 'path', { unique: false });
      }

      // Peak files store
      if (!db.objectStoreNames.contains(STORE_PEAK_FILES)) {
        const peakStore = db.createObjectStore(STORE_PEAK_FILES, { keyPath: 'id' });
        peakStore.createIndex('audioFileId', 'audioFileId', { unique: false });
      }

      // Metadata store
      if (!db.objectStoreNames.contains(STORE_METADATA)) {
        const metadataStore = db.createObjectStore(STORE_METADATA, { keyPath: 'id' });
        metadataStore.createIndex('sessionId', 'sessionId', { unique: false });
      }

      // Projects store
      if (!db.objectStoreNames.contains(STORE_PROJECTS)) {
        const projectsStore = db.createObjectStore(STORE_PROJECTS, { keyPath: 'id' });
        projectsStore.createIndex('name', 'name', { unique: false });
        projectsStore.createIndex('updatedAt', 'updatedAt', { unique: false });
      }
    };
  });
}

/**
 * SessionSave
 */
export async function saveSession(session: SessionData, sessionId: string): Promise<void> {
  const db = await initDB();
  const xml = serializeSessionXML(session);

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_SESSIONS], 'readwrite');
    const store = transaction.objectStore(STORE_SESSIONS);

    const sessionData = {
      id: sessionId,
      name: session.name,
      xml: xml,
      data: session,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const request = store.put(sessionData);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      reject(new Error('Failed to save session'));
    };
  });
}

/**
 * SessionLoad
 */
export async function loadSession(sessionId: string): Promise<SessionData | null> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_SESSIONS], 'readonly');
    const store = transaction.objectStore(STORE_SESSIONS);
    const request = store.get(sessionId);

    request.onsuccess = () => {
      const result = request.result;
      if (!result) {
        resolve(null);
        return;
      }

 // XMLfrom
      const session = parseSessionXML(result.xml);
      resolve(session);
    };

    request.onerror = () => {
      reject(new Error('Failed to load session'));
    };
  });
}

/**
 * SessionlistGet
 */
export async function listSessions(): Promise<Array<{ id: string; name: string; createdAt: string; updatedAt: string }>> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_SESSIONS], 'readonly');
    const store = transaction.objectStore(STORE_SESSIONS);
    const index = store.index('createdAt');
    const request = index.getAll();

    request.onsuccess = () => {
      const sessions = request.result.map((item: any) => ({
        id: item.id,
        name: item.name,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      }));
      resolve(sessions);
    };

    request.onerror = () => {
      reject(new Error('Failed to list sessions'));
    };
  });
}

/**
 * SessionDelete
 */
export async function deleteSession(sessionId: string): Promise<void> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_SESSIONS], 'readwrite');
    const store = transaction.objectStore(STORE_SESSIONS);
    const request = store.delete(sessionId);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      reject(new Error('Failed to delete session'));
    };
  });
}

/**
 * Audio filedataTypeDefinition
 */
export interface AudioFileData {
  id: string;
  sessionId: string;
  name: string;
  data: ArrayBuffer;
  sampleRate: number;
  channels: number;
  length: number;
  duration: number;
  createdAt: string;
}

/**
 * Audio fileSave（expand）
 */
export async function saveAudioFile(audioFile: Omit<AudioFileData, 'createdAt'>): Promise<void> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_AUDIO_FILES], 'readwrite');
    const store = transaction.objectStore(STORE_AUDIO_FILES);

    const fileData: AudioFileData = {
      ...audioFile,
      createdAt: new Date().toISOString(),
    };

    const request = store.put(fileData);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      reject(new Error('Failed to save audio file'));
    };
  });
}

/**
 * Audio fileLoad
 */
export async function loadAudioFileData(fileId: string): Promise<AudioFileData | null> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_AUDIO_FILES], 'readonly');
    const store = transaction.objectStore(STORE_AUDIO_FILES);
    const request = store.get(fileId);

    request.onsuccess = () => {
      resolve(request.result || null);
    };

    request.onerror = () => {
      reject(new Error('Failed to load audio file'));
    };
  });
}

/**
 * SessionAudio filelistGet
 */
export async function listAudioFilesForSession(sessionId: string): Promise<AudioFileData[]> {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_AUDIO_FILES], 'readonly');
    const store = transaction.objectStore(STORE_AUDIO_FILES);
    const index = store.index('sessionId');
    
    const request = index.getAll(sessionId);
    
    request.onsuccess = () => {
      resolve(request.result || []);
    };
    
    request.onerror = () => {
      reject(new Error('Failed to list audio files'));
    };
  });
}

export async function deleteAudioFile(fileId: string): Promise<void> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_AUDIO_FILES], 'readwrite');
    const store = transaction.objectStore(STORE_AUDIO_FILES);
    const request = store.delete(fileId);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      reject(new Error('Failed to delete audio file'));
    };
  });
}
